<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import {
  MAX_SHAREPOINT_SITE_URLS,
  SHAREPOINT_ERROR_MESSAGES,
} from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import SharepointSiteDeleteModal from '@/components/admin/user-documents/sharepoint-site-delete-modal.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import { SharepointSite } from '@/types';

const { createSnackbar } = useSnackbar();

const tenantId = ref('');
const clientId = ref('');
const thumbprint = ref('');
const router = useRouter();
const route = useRoute();
const storageStatus = route.query.storageStatus;
const isSending = ref(false);

type Props = {
  initSharepointSites?: SharepointSite[];
};
const props = withDefaults(defineProps<Props>(), {
  initSharepointSites: () => [],
});

// アクションメニュー
const openActionMenu = (index: number) => {
  closeActionMenu();
  siteForms.value[index].isOpenActionMenu = true;
};
const closeActionMenu = () =>
  siteForms.value.forEach(form => (form.isOpenActionMenu = false));

// フォームの状態管理
const siteForms = ref<
  {
    site: SharepointSite;
    isOpenEditForm: boolean;
    isOpenActionMenu: boolean;
    isOpenDeleteModal: boolean;
  }[]
>(
  props.initSharepointSites.map(site => ({
    site,
    isOpenEditForm: false,
    isOpenActionMenu: false,
    isOpenDeleteModal: false,
  })),
);

// 初期化
watch(
  () => props.initSharepointSites,
  initSharepointSites => {
    siteForms.value = initSharepointSites.map(site => ({
      site,
      isOpenEditForm: false,
      isOpenActionMenu: false,
      isOpenDeleteModal: false,
    }));
  },
);

const closeAllSiteForms = () => {
  siteForms.value.forEach(siteForm => (siteForm.isOpenEditForm = false));
};

// URLの編集フォーム
const editingFormValue = ref('');
const editingIndex = ref<number | null>(null);
const updateSiteForm = () => {
  if (editingIndex.value === null) return;
  siteForms.value[editingIndex.value].site.site_url = editingFormValue.value;
  siteForms.value[editingIndex.value].isOpenEditForm = false;
  editingFormValue.value = '';
  editingIndex.value = null;
};
const cancelEditForm = (index: number) => {
  siteForms.value[index].isOpenEditForm = false;
  editingFormValue.value = '';
  editingIndex.value = null;
};
const openEditForm = (index: number) => {
  closeAllSiteForms();
  siteForms.value[index].isOpenEditForm = true;
  editingFormValue.value = siteForms.value[index].site.site_url;
  editingIndex.value = index;
};
const isEditFormButtonDisabled = computed(() => {
  // 空または重複している場合は無効にする（現在編集中の行を除く）
  if (editingFormValue.value === '') return true;
  return siteForms.value.some(
    (form, idx) =>
      idx !== editingIndex.value &&
      form.site.site_url === editingFormValue.value,
  );
});

// URLの追加フォーム
const addFormValue = ref('');
const addSiteForm = () => {
  closeAllSiteForms();
  siteForms.value.push({
    site: { id: null, site_url: addFormValue.value },
    isOpenEditForm: false,
    isOpenActionMenu: false,
    isOpenDeleteModal: false,
  });
  addFormValue.value = '';
};
const isAddFormButtonDisabled = computed(
  () =>
    addFormValue.value === '' ||
    siteForms.value.some(form => form.site.site_url === addFormValue.value),
);
const isAddFormVisible = computed(
  () => siteForms.value.length < MAX_SHAREPOINT_SITE_URLS,
);

// 削除モーダル
const openDeleteModal = (index: number) => {
  closeActionMenu();
  closeAllSiteForms();
  siteForms.value[index].isOpenDeleteModal = true;
};
const closeDeleteModal = () => {
  siteForms.value.forEach(form => (form.isOpenDeleteModal = false));
};
const deleteSiteForm = (index: number) => {
  siteForms.value.splice(index, 1);
  closeDeleteModal();
};

onMounted(() => {
  document.addEventListener('click', closeActionMenu);
});
onUnmounted(() => {
  document.removeEventListener('click', closeActionMenu);
});

const createSharepointCredential = async () => {
  isSending.value = true;
  try {
    const sharepoint_sites = siteForms.value.map(form => ({
      site_url: form.site.site_url,
    }));

    if (storageStatus === 'CREDENTIAL_INVALID') {
      await api.putSharepointCredential({
        clientId: clientId.value,
        tenantId: tenantId.value,
        thumbprint: thumbprint.value,
      });
      // PUT の後に別途サイト情報を更新
      // すべてのサイトを一括更新APIに送信（新規・既存問わず）
      await api.putSharepointSites(sharepoint_sites);
    } else {
      // POST では sharepoint_sites を送信
      await api.postSharepointCredential({
        clientId: clientId.value,
        tenantId: tenantId.value,
        thumbprint: thumbprint.value,
        sharepointSites: sharepoint_sites,
      });
    }

    await router.push({ name: 'sharepointIndex' });
  } catch (error) {
    let errorMessage = '認証情報の登録に失敗しました';
    if (isAxiosError(error)) {
      const serverError: string = error.response?.data.error;
      if (serverError in SHAREPOINT_ERROR_MESSAGES) {
        errorMessage = SHAREPOINT_ERROR_MESSAGES[serverError];
      }
    }
    createSnackbar({
      message: errorMessage,
      type: 'error',
    });
    // eslint-disable-next-line no-console
    console.error(error);
  }
  isSending.value = false;
};

const isDisabledSubmitButton = computed<boolean>(
  () =>
    tenantId.value.trim() === '' ||
    clientId.value.trim() === '' ||
    thumbprint.value.trim() === '' ||
    isSending.value,
);
</script>

<template>
  <div class="user-documents-admin">
    <Header
      title="SharePointを認証"
      header-width="min(912px, 100%)"
      is-one-line
    ></Header>
    <div class="form-content">
      <div class="content">
        <div class="content-header">
          <div class="title">
            <span class="c-title-bold--m">認証項目</span>
          </div>
          <div class="sharepoint-credential-form-content">
            <div class="form-group">
              <label class="c-title-bold--s" for="client-id-form">
                クライアントID
              </label>
              <div class="form-input-container">
                <input
                  v-model="clientId"
                  id="client-id-form"
                  class="sharepoint-input"
                  type="text"
                  maxlength="36"
                />
              </div>
            </div>

            <div class="form-group">
              <label class="c-title-bold--s" for="tenant-id-form">
                テナントID
              </label>
              <div class="form-input-container">
                <input
                  id="tenant-id-form"
                  v-model="tenantId"
                  class="sharepoint-input"
                  type="text"
                  maxlength="36"
                />
              </div>
            </div>

            <div>
              <label class="c-title-bold--s" for="thumbprint-form">
                証明書サムプリント
              </label>
              <div class="spacing-08"></div>
              <input
                id="thumbprint-form"
                v-model="thumbprint"
                class="sharepoint-input"
                type="text"
                maxlength="40"
              />
            </div>

            <div class="form-group">
              <div class="spacing-24"></div>
              <div class="spacing-08"></div>
              <div class="site-header">
                <div class="site-header-top">
                  <label class="c-title-bold--s">サイト</label>
                  <div class="c-text-regular--s site-count">
                    ・{{ siteForms.length }}件/{{ MAX_SHAREPOINT_SITE_URLS }}件
                  </div>
                </div>
                <div class="site-notice c-text-regular--s">
                  サイトURLは正規化して保存されます
                </div>
              </div>

              <div class="sites-container">
                <div
                  v-for="(siteForm, i) in siteForms"
                  :key="i"
                  class="site-item"
                >
                  <div class="site-input-container">
                    <input
                      v-if="siteForm.isOpenEditForm"
                      type="text"
                      class="sharepoint-input site-url-input"
                      v-model="editingFormValue"
                      placeholder="https://"
                    />
                    <div v-else class="site-url-display">
                      <span class="site-url-text">{{
                        siteForm.site.site_url
                      }}</span>
                      <div
                        class="action-button"
                        @click.stop="openActionMenu(i)"
                      >
                        <DgrIcon name="ellipsis-h" size="small" />
                      </div>
                    </div>
                    <div
                      v-if="siteForm.isOpenActionMenu"
                      class="action-menu c-text-regular--m"
                    >
                      <div class="action-menu-item" @click="openEditForm(i)">
                        編集
                      </div>
                      <div class="action-menu-item" @click="openDeleteModal(i)">
                        削除
                      </div>
                    </div>
                  </div>
                  <div class="edit-buttons" v-if="siteForm.isOpenEditForm">
                    <button
                      class="c-btn c-btn--auto c-btnOutline"
                      @click="cancelEditForm(i)"
                    >
                      キャンセル
                    </button>
                    <button
                      class="c-btn c-btn--auto c-btn--AnewsPrimary"
                      :disabled="isEditFormButtonDisabled"
                      :class="{ disabled: isEditFormButtonDisabled }"
                      @click="updateSiteForm()"
                    >
                      保存
                    </button>
                  </div>
                  <SharepointSiteDeleteModal
                    v-if="siteForm.isOpenDeleteModal"
                    v-model:open="siteForm.isOpenDeleteModal"
                    :url="siteForm.site.site_url"
                    @submit="deleteSiteForm(i)"
                  />
                </div>

                <div class="site-add-form" v-if="isAddFormVisible">
                  <div class="site-input-container">
                    <input
                      type="text"
                      class="sharepoint-input site-url-input"
                      v-model="addFormValue"
                      placeholder="https://"
                    />
                    <button
                      class="c-btn c-btn--auto c-btn--AnewsPrimary add-button"
                      :class="{ disabled: isAddFormButtonDisabled }"
                      :disabled="isAddFormButtonDisabled"
                      @click="addSiteForm"
                    >
                      追加
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="form-footer">
      <div class="form-footer-inner">
        <button
          class="c-btn c-btn--auto c-btn--AnewsPrimary"
          :disabled="isDisabledSubmitButton"
          :class="{ disabled: isDisabledSubmitButton }"
          @click="createSharepointCredential"
        >
          認証
        </button>
        <button class="c-btn c-btn--auto c-btnOutline" @click="router.back()">
          キャンセル
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user-documents-admin {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  padding: 0 !important;

  .form-content {
    box-sizing: border-box;
    width: 100%;
    max-width: calc(912px + 48px);
    margin: 0 auto;
    padding: 24px;
  }

  // 内容が短くても画面の最下部に置く
  .form-footer {
    position: sticky;
    bottom: 0;
    margin-top: auto;
    padding: 16px 24px;
    background: $color-white;
    box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);
    .form-footer-inner {
      display: flex;
      gap: 8px;
      max-width: 912px;
      margin: 0 auto;
    }
  }

  .content-header {
    flex-direction: column;

    .title {
      margin-bottom: 16px;
    }
  }

  .sharepoint-credential-form-content {
    padding: 24px;
    border-radius: 4px;
    background-color: #fff;
    border: 1px solid $color-border;
  }

  .form-group {
    margin-bottom: 24px;

    &:last-of-type {
      margin-bottom: 0;
    }

    label {
      display: block;
      margin-bottom: 8px;
    }
  }

  .form-input-container {
    width: 100%;
  }

  .site-header {
    display: block;
    padding-bottom: 12px;
    border-bottom: 1px solid $color-gray400;

    .site-header-top {
      display: flex;
      align-items: baseline;
      gap: 4px;
    }

    .site-count {
      color: $color-gray600;
    }

    .site-notice {
      color: $color-gray1000;
      margin-top: 8px;
    }
  }

  .sites-container {
    .site-item {
      padding: 8px 0;
      border-bottom: 1px solid $color-gray400;
      position: relative;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .site-input-container {
      display: flex;
      align-items: center;
      position: relative;
      width: 100%;

      .site-url-input {
        width: 100%;
        padding-right: 48px;
      }

      .site-url-display {
        display: flex;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
        position: relative;
        margin: 0;

        .site-url-text {
          flex: 1;
          word-break: break-all;
          color: $color-gray1000;
          font-size: 14px;
          line-height: 1.4;
          padding-right: 32px;
          margin: 0;
        }
      }

      .action-button {
        display: flex;
        justify-content: center;
        padding: 8px;
        border-radius: 4px;
        &:hover {
          background-color: $color-gray200;
        }
      }
    }

    .edit-buttons {
      margin-top: 4px;
      display: flex;
      justify-content: flex-start;
      gap: 4px;
    }
  }

  .site-add-form {
    margin-top: 8px;
    display: block;

    .site-input-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .add-button {
      white-space: nowrap;
      min-width: 60px;
    }
  }

  .action-menu {
    position: absolute;
    right: 0;
    top: 100%;
    width: 98px;
    background: $color-white;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 17px 0;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .action-menu-item {
      padding: 0 12px;
      cursor: pointer;

      &:hover {
        background: $color-gray200;
      }
    }
  }

  .sharepoint-input {
    width: 100%;
    box-sizing: border-box;
    height: 40px;
  }
}
</style>
